import { toInteger } from 'lodash';
import { SliderInput } from '@meitu/saas-ui';
import { resolveVectorTextPaddingLimits, type VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { type WidgetCommonProps } from '../../types';
import './style.less';

type Props = WidgetCommonProps;

export const TextPadding: React.FC<Props> = ({ layerId }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return null;

        const { padding } = textInfo;

        return {
            width: layer.__proactiveWidth || layer.width,
            height: layer.__proactiveHeight || layer.height,
            padding,
            textInfo,
        };
    });

    const width = props?.width || 0;
    const height = props?.height || 0;
    const padding = props?.padding || { top: 0, right: 0, bottom: 0, left: 0 };
    const horizontalPadding = padding.left || 0;
    const verticalPadding = padding.top || 0;

    const min = 0;
    const limits = props
        ? resolveVectorTextPaddingLimits({ width, height }, props.textInfo)
        : { horizontal: 0, vertical: 0 };

    const handleChangePadding = (
        value: number,
        options: { kind: 'horizontal' | 'vertical'; writeHistory: boolean },
    ) => {
        if (!props) return;

        const nextPadding = { ...padding };
        const integerValue = toInteger(value);

        if (options.kind === 'horizontal') {
            nextPadding.left = integerValue;
            nextPadding.right = integerValue;
        } else {
            nextPadding.top = integerValue;
            nextPadding.bottom = integerValue;
        }

        editor
            .getInstance<VectorLayer>('VectorLayer')
            .setTextPadding(layerId, nextPadding, { writeHistory: options.writeHistory });
    };

    return (
        <div className="text-padding">
            <LabelAction title={$t((v) => v.panel.vectorPanel.textInfo.vPadding)} align>
                <SliderInput
                    min={min}
                    max={limits.vertical}
                    step={1}
                    showStepBtn
                    value={verticalPadding}
                    type="horizontal"
                    onChange={(value) => handleChangePadding(value, { kind: 'vertical', writeHistory: false })}
                    onAfterChange={(value) => handleChangePadding(value, { kind: 'vertical', writeHistory: true })}
                />
            </LabelAction>

            <LabelAction title={$t((v) => v.panel.vectorPanel.textInfo.hPadding)} align>
                <SliderInput
                    min={min}
                    max={limits.horizontal}
                    step={1}
                    showStepBtn
                    value={horizontalPadding}
                    type="horizontal"
                    onChange={(value) => handleChangePadding(value, { kind: 'horizontal', writeHistory: false })}
                    onAfterChange={(value) => handleChangePadding(value, { kind: 'horizontal', writeHistory: true })}
                />
            </LabelAction>
        </div>
    );
};
